import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { makeStyles } from '@material-ui/core/styles'
import { Icon } from '@iconify/react'
import { track } from '@amplitude/analytics-browser'
import { AskAiBox, Button, MetricCard, colors } from 'src/components/app-ui'
import {
  getCollectionAnalyticsAction,
  exportCollectionAnalyticsAction,
} from 'src/reducers/course-collection/action'

const AI_QUESTIONS = [
  'Where do learners drop off in this path?',
  'Which course takes the longest?',
  'Who finished the whole path this month?',
]

const useStyles = makeStyles({
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px',
  },
  selectWrap: {
    position: 'relative',
    width: 340,
    maxWidth: '100%',
  },
  select: {
    width: '100%',
    padding: '10px 36px 10px 12px',
    border: `1px solid ${colors.borderSoft}`,
    borderRadius: '8px',
    outline: 'none',
    background: '#FFFFFF',
    appearance: 'none',
    WebkitAppearance: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '14px',
    color: colors.navy,
  },
  chevron: {
    position: 'absolute',
    top: '50%',
    right: 12,
    transform: 'translateY(-50%)',
    color: colors.muted,
    pointerEvents: 'none',
  },
  download: {
    borderColor: colors.primary,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))',
    gap: '16px',
  },
})

const formatScore = score =>
  score == null || score === 'NaN' ? '-' : `${score} %`

/** Metric definitions; `pick` reads the value from the analytics response. */
const METRICS = [
  {
    label: 'Eligible learners',
    hint: 'Learners allowed to open this path under its authentication.',
    icon: 'tabler:chart-bar',
    iconColor: '#5DADFF',
    pick: data => (data?.assigned == null ? 'All' : data.assigned),
  },
  {
    label: 'Engaged learners',
    hint: 'Learners who opened at least one page.',
    icon: 'tabler:chart-bar',
    iconColor: colors.primary,
    pick: data => data?.engaged ?? 0,
  },
  {
    label: 'Learners who completed',
    hint: 'Learners who finished every course in the path.',
    courseHint: 'Learners who finished this course.',
    icon: 'tabler:chart-bar',
    iconColor: colors.success,
    pick: data => data?.completed ?? 0,
  },
  {
    label: 'Learners with badges',
    hint: 'Learners who earned at least one badge, including fail badges.',
    icon: 'tabler:badge',
    iconColor: colors.subtle,
    pick: data => data?.badgeCount ?? 0,
  },
  {
    label: 'Learners with certificates',
    hint: 'Learners who received a certificate.',
    icon: 'tabler:certificate',
    iconColor: colors.subtle,
    pick: data => data?.certificateCount ?? 0,
  },
  {
    label: 'Average success score',
    hint: 'Average quiz score across attempts.',
    icon: 'tabler:help-square',
    iconColor: colors.subtle,
    pick: data => formatScore(data?.averageScore),
  },
]

const AnalyticsTab = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { id } = useParams()
  const courses = useSelector(state => state.collection.courses)
  const [courseId, setCourseId] = useState('')
  const [data, setData] = useState(null)

  useEffect(() => {
    dispatch(getCollectionAnalyticsAction(id, courseId, setData))
  }, [dispatch, id, courseId])

  const download = () => {
    track('export_collection_analytics_clicked')
    dispatch(exportCollectionAnalyticsAction(id))
  }

  return (
    <>
      <div className={classes.toolbar}>
        <div className={classes.selectWrap}>
          <select
            className={classes.select}
            value={courseId}
            onChange={event => setCourseId(event.target.value)}
          >
            <option value="">All courses</option>
            {(courses || []).map(course => (
              <option key={course.id} value={course.id}>
                {course.name}
              </option>
            ))}
          </select>
          <Icon
            icon="tabler:chevron-down"
            width={16}
            height={16}
            className={classes.chevron}
          />
        </div>
        <Button
          variant="outline"
          size="small"
          icon="tabler:download"
          className={classes.download}
          onClick={download}
        >
          Download full report
        </Button>
      </div>
      <div className={classes.grid}>
        {METRICS.map(metric => (
          <MetricCard
            key={metric.label}
            label={metric.label}
            hint={(courseId && metric.courseHint) || metric.hint}
            value={metric.pick(data)}
            icon={metric.icon}
            iconColor={metric.iconColor}
          />
        ))}
      </div>
      <AskAiBox questions={AI_QUESTIONS} />
    </>
  )
}

export default AnalyticsTab
